import { useState } from 'react'

import { keyframes } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { AI_CHATBOT_CONFIG } from '../core/engine/config'
import { useAiChatBotStore } from '../core/store/useAiChatBotStore'
import { useConversationStore } from '../core/store/useConversationStore'
import { theme } from '@/Atomic/assets/theme'
import { Icon } from '@/Atomic/Atoms/Icon'

// --- Styled components ---

const OverlayHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.space.s} ${theme.space.s} ${theme.space.s} ${theme.space.m};
  border-bottom: 1px solid ${theme.colors.border.line};
  flex-shrink: 0;
`

const OverlayTitle = styled.span`
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.04em;
`

const CloseButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  color: ${theme.colors.text.secondary};
  transition: background 0.1s ease;

  &:hover {
    background: ${theme.colors.background.hover};
  }
`

const Container = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`

const Empty = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.text.disabled};
  font-size: 0.875rem;
  padding: ${theme.space.xl};
  text-align: center;
`

const Item = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.s};
  padding: ${theme.space.xs} ${theme.space.m} ${theme.space.xs} ${theme.space.s};
  cursor: pointer;
  border-bottom: 1px solid ${theme.colors.border.line};
  transition: background 0.1s ease;

  &:last-of-type {
    border-bottom: none;
  }

  &:hover {
    background: rgba(0, 0, 0, 0.05);
  }
`

const DeletedItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.space.xs} ${theme.space.m} ${theme.space.xs} ${theme.space.s};
  border-bottom: 1px solid ${theme.colors.border.line};
  overflow: hidden;
  min-height: 48px;

  &:last-of-type {
    border-bottom: none;
  }
`

const CheckSlot = styled.div`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`

const ItemPreview = styled.div`
  font-size: 0.875rem;
  color: ${theme.colors.text.primary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const ItemMeta = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.space.xs};
  margin-top: 2px;
`

const ItemTime = styled.span`
  font-size: 0.75rem;
  color: ${theme.colors.text.secondary};
`

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  padding: 0;
  background: none;
  border: none;
  border-radius: ${theme.radii.sm};
  cursor: pointer;
  transition: background 0.1s ease;

  &:hover {
    background: ${theme.colors.danger.subtle};
  }
`

const UndoButton = styled.button`
  flex-shrink: 0;
  background: none;
  border: none;
  padding: 0 ${theme.space.xs};
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  color: ${theme.colors.text.primary};
  text-decoration: underline;
  opacity: 1;
`

const drainProgress = keyframes`
  from { transform: scaleX(1); }
  to   { transform: scaleX(0); }
`

const InlineProgressBar = styled.div<{ durationMs: number }>`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  height: 2px;
  background: ${theme.colors.danger.main};
  transform-origin: left;
  animation: ${drainProgress} ${({ durationMs }) => durationMs}ms linear
    forwards;
`

// --- Helpers ---

function formatRelativeTime(
  ms: number,
  t: (key: string, opts?: Record<string, unknown>) => string,
): string {
  const diff = Date.now() - ms
  const minutes = Math.floor(diff / 60_000)
  const hours = Math.floor(diff / 3_600_000)
  const days = Math.floor(diff / 86_400_000)
  if (minutes < 1) {
    return t('components.aiChatBot.history.justNow')
  }
  if (minutes < 60) {
    return t('components.aiChatBot.history.minutesAgo', { count: minutes })
  }
  if (hours < 24) {
    return t('components.aiChatBot.history.hoursAgo', { count: hours })
  }
  return t('components.aiChatBot.history.daysAgo', { count: days })
}

// --- Component ---

interface ConversationHistoryProps {
  onSelect: (id: string) => void
  onClose: () => void
}

interface PendingDelete {
  timeoutId: ReturnType<typeof setTimeout>
}

export function ConversationHistory({
  onSelect,
  onClose,
}: ConversationHistoryProps) {
  const { t } = useTranslation()
  const activeId = useAiChatBotStore((s) => s.activeId)
  const conversations = useConversationStore((s) => s.conversations)
  const { deleteConversation } = useConversationStore.getState()
  const [pendingDeletes, setPendingDeletes] = useState<
    Map<string, PendingDelete>
  >(new Map())

  const sorted = Object.entries(conversations).sort(
    (a, b) => b[1].updatedAt - a[1].updatedAt,
  )

  function handleDelete(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    const existing = pendingDeletes.get(id)
    if (existing) clearTimeout(existing.timeoutId)
    const timeoutId = setTimeout(() => {
      deleteConversation(id)
      setPendingDeletes((prev) => {
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    }, AI_CHATBOT_CONFIG.conversationStore.undoDeleteWindowMs)
    setPendingDeletes((prev) => new Map(prev).set(id, { timeoutId }))
  }

  function handleUndo(id: string, e: React.MouseEvent) {
    e.stopPropagation()
    const pending = pendingDeletes.get(id)
    if (!pending) return
    clearTimeout(pending.timeoutId)
    setPendingDeletes((prev) => {
      const next = new Map(prev)
      next.delete(id)
      return next
    })
  }

  return (
    <>
      <OverlayHeader>
        <OverlayTitle>{t('components.aiChatBot.history.title')}</OverlayTitle>
        <CloseButton
          type="button"
          onClick={onClose}
          aria-label={t('components.aiChatBot.history.close')}
        >
          <Icon icon="chevron-up" />
        </CloseButton>
      </OverlayHeader>
      <Container>
        {sorted.length === 0 ? (
          <Empty>{t('components.aiChatBot.history.empty')}</Empty>
        ) : (
          sorted.map(([id, entry]) => {
            const isPending = pendingDeletes.has(id)

            if (isPending) {
              return (
                <DeletedItem key={id}>
                  <ItemTime>
                    {t('components.aiChatBot.history.deleted')}
                  </ItemTime>
                  <UndoButton type="button" onClick={(e) => handleUndo(id, e)}>
                    {t('components.aiChatBot.history.undoDelete')}
                  </UndoButton>
                  <InlineProgressBar
                    key={`progress-${id}`}
                    durationMs={
                      AI_CHATBOT_CONFIG.conversationStore.undoDeleteWindowMs
                    }
                  />
                </DeletedItem>
              )
            }

            return (
              <Item key={id} onClick={() => onSelect(id)}>
                <CheckSlot>
                  {id === activeId && (
                    <Icon icon="check" size="sm" color="strong" />
                  )}
                </CheckSlot>
                <ItemContent>
                  <ItemPreview>
                    {entry.preview ||
                      t('components.aiChatBot.history.untitled')}
                  </ItemPreview>
                  <ItemMeta>
                    <ItemTime>
                      {formatRelativeTime(entry.updatedAt, t)}
                    </ItemTime>
                  </ItemMeta>
                </ItemContent>
                <DeleteButton
                  type="button"
                  aria-label={t(
                    'components.aiChatBot.history.deleteConversation',
                  )}
                  onClick={(e) => handleDelete(id, e)}
                >
                  <Icon icon="bin" size="sm" color="danger" />
                </DeleteButton>
              </Item>
            )
          })
        )}
      </Container>
    </>
  )
}
